import { z } from "zod";

/** A two-decimal money string, exactly as the API writes it ("450.00"). Never a number. */
const moneySchema = z.string().regex(/^-?\d+\.\d{2}$/, "Expected a two-decimal amount");

/** A UTC instant from the API, checked for parseability so a bad value fails here and not on screen. */
const instantSchema = z
  .string()
  .refine((value) => !Number.isNaN(Date.parse(value)), "Expected an ISO instant");

/**
 * `AppointmentStatus` (Appointment.cs), serialised by name. The actions a customer is offered route
 * on it, so an unknown value is a contract break and fails the parse rather than hiding every button.
 */
export const myAppointmentStatusSchema = z.enum([
  "Requested",
  "Confirmed",
  "Arrived",
  "CheckedIn",
  "InQueue",
  "InService",
  "Completed",
  "Cancelled",
  "Rejected",
  "NoShow",
]);

/** `AppointmentServiceStatus`: where one service of the visit has got to. */
export const myAppointmentLineStatusSchema = z.enum([
  "Scheduled",
  "InService",
  "Completed",
  "Cancelled",
  "NoShow",
]);

/** Mirrors `MyAppointmentLine` (GetMyAppointmentsQuery.cs). Name, price and stylist are the booking's snapshot. */
export const myAppointmentLineSchema = z.object({
  id: z.string().min(1),
  sequence: z.number().int(),
  serviceId: z.string().min(1),
  serviceName: z.string(),
  /** Null for a walk-in who asked for whoever is free, until a stylist calls them (FR-SA-063). */
  staffId: z.string().min(1).nullable(),
  staffName: z.string().nullable(),
  startsAt: instantSchema,
  endsAt: instantSchema,
  durationMinutes: z.number().int(),
  price: moneySchema,
  status: myAppointmentLineStatusSchema,
  /** Added by the salon after the booked service, such as a bridal trim (FR-SA-185). */
  isExtra: z.boolean().optional(),
});

/** Mirrors `MyVisitProduct`: a product bought on the visit (FR-SA-186). Money stays a string. */
export const myVisitProductSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  unitPrice: moneySchema,
  quantity: z.number().int().min(1),
  amount: moneySchema,
});

/**
 * Mirrors `MyAppointmentItem` (TDD-SA FR-SA-052, FR-SA-081). A customer shape: no staff note, and
 * `customerNote` is what the customer typed when booking. `source` is kept a string because no
 * screen routes on it.
 */
export const myAppointmentItemSchema = z.object({
  id: z.string().min(1),
  branchId: z.string().min(1),
  /** Where the visit is, at a salon with several branches (FR-SA-245). Older servers send neither. */
  branchName: z.string().nullable().optional(),
  branchSlug: z.string().nullable().optional(),
  startsAt: instantSchema,
  endsAt: instantSchema,
  status: myAppointmentStatusSchema,
  source: z.string(),
  isUpcoming: z.boolean(),
  customerNote: z.string().nullable(),
  total: moneySchema,
  lines: z.array(myAppointmentLineSchema),
  /** Whether the customer has already rated this visit (BR-SA-015: once per visit). */
  hasFeedback: z.boolean().default(false),
  /** Products bought on the visit, which `total` includes (FR-SA-186; owner decision 2026-10-27). */
  products: z.array(myVisitProductSchema).default([]),
});

/**
 * Mirrors `MyQueuePosition` (GetMyQueuePositionQuery.cs, FR-SA-065). `estimatedStartAt` is null when
 * the branch has nobody who could take the customer, which the screen says rather than guessing.
 */
export const myQueuePositionSchema = z.object({
  appointmentId: z.string().min(1),
  queueEntryId: z.string().min(1),
  position: z.number().int(),
  status: z.string(),
  joinedAt: instantSchema,
  waitedMinutes: z.number().int(),
  estimatedStartAt: instantSchema.nullable(),
});

/** Mirrors `FrequentServiceItem` (GetFrequentServicesQuery.cs, FR-SA-083): today's name and price. */
export const frequentServiceSchema = z.object({
  serviceId: z.string().min(1),
  name: z.string(),
  durationMinutes: z.number().int(),
  price: moneySchema,
  timesBooked: z.number().int(),
  lastBookedAt: instantSchema,
});

export const frequentServicesSchema = z.array(frequentServiceSchema);

/** `MyAppointmentScope`, bound by name on the query string. */
export const myAppointmentScopeSchema = z.enum(["Upcoming", "Past"]);

/** The page's state, read from the URL so the back button and a shared link both work. */
export const myAppointmentsViewSchema = z.object({
  tab: z.enum(["upcoming", "past"]).catch("upcoming"),
  page: z.coerce.number().int().min(1).catch(1),
});

/* ---------- forms ---------- */

/** `Appointment.ReasonMaxLength`. */
export const REASON_MAX = 500;

/** `Feedback.CommentMaxLength`. */
export const COMMENT_MAX = 2000;

export const cancelFormSchema = z.object({
  reason: z.string().trim().max(REASON_MAX, `At most ${REASON_MAX} characters`),
});

/**
 * `SubmitFeedbackRequest`: the visit 1 to 5, the stylist 1 to 5 or nothing, and an optional comment.
 * Zero stands for "not chosen" in the form, because that is what `RatingInput` shows as empty.
 */
export const feedbackFormSchema = z.object({
  rating: z.number().int().min(1, "Choose a rating for your visit").max(5),
  staffRating: z.number().int().min(0).max(5),
  comment: z.string().trim().max(COMMENT_MAX, `At most ${COMMENT_MAX} characters`),
});

export type MyAppointmentStatus = z.infer<typeof myAppointmentStatusSchema>;
export type MyAppointmentLine = z.infer<typeof myAppointmentLineSchema>;
export type MyAppointmentItem = z.infer<typeof myAppointmentItemSchema>;
export type MyQueuePosition = z.infer<typeof myQueuePositionSchema>;
export type FrequentService = z.infer<typeof frequentServiceSchema>;
export type MyAppointmentScope = z.infer<typeof myAppointmentScopeSchema>;
export type MyAppointmentsView = z.infer<typeof myAppointmentsViewSchema>;
export type CancelFormValues = z.infer<typeof cancelFormSchema>;
export type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

/** `POST .../reschedule`: an empty or missing service list keeps the services already booked. */
export interface RescheduleMyAppointmentRequest {
  startsAt: string;
  services?: { serviceId: string; preferredStaffId?: string | null }[];
}

/** `SubmitFeedbackRequest` (MyFeedbackController.cs). */
export interface SubmitFeedbackRequest {
  rating: number;
  staffRating: number | null;
  comment: string | null;
}
